import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Wallet, ArrowDownToLine, ArrowUpFromLine, Percent } from "lucide-react";
import { useApi, brl, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";

const Stat = ({ label, value, icon: Icon, tid, accent }) => (
  <div className="card" data-testid={tid}>
    <div className="flex items-center justify-between">
      <p className="caption">{label}</p>
      <Icon size={16} className={accent} />
    </div>
    <p className="mt-3 font-mono text-2xl font-semibold text-slate-50">{value}</p>
  </div>
);

export const TxTable = ({ rows, empty = "Nenhuma transação ainda." }) => (
  <div className="overflow-x-auto">
    <table className="tx-table" data-testid="transactions-table">
      <thead><tr><th>Tipo</th><th>Descrição</th><th>Origem</th><th>Valor</th><th>Status</th><th>Data</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">{empty}</td></tr>}
        {rows.map((t) => (
          <tr key={t.id} data-testid={`tx-row-${t.id}`}>
            <td>{t.type === "deposit" ? <span className="text-emerald-400">Entrada</span> : <span className="text-cyan-400">Saída</span>}</td>
            <td className="max-w-[220px] truncate">{t.description}{t.reference ? <span className="ml-1 text-xs text-slate-500">#{t.reference}</span> : null}</td>
            <td className="uppercase text-xs text-slate-400">{t.source}</td>
            <td className="font-mono">{t.type === "deposit" ? "+" : "−"} {brl(t.amount)}</td>
            <td><StatusBadge status={t.status} /></td>
            <td className="whitespace-nowrap text-xs text-slate-400">{fmtDate(t.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setData(r.data)).catch(() => {}); }, [api]);
  const b = data?.balance;

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="caption">Visão geral</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Painel principal</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/recebimentos"><Button className="btn-primary" data-testid="quick-new-deposit-button">Novo recebimento</Button></Link>
          <Link to="/saques"><Button className="btn-ghost" data-testid="quick-new-payout-button">Solicitar saque</Button></Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Saldo disponível" value={brl(b?.available)} icon={Wallet} tid="stat-card-balance-brl" accent="text-emerald-400" />
        <Stat label="Total recebido" value={brl(b?.total_received)} icon={ArrowDownToLine} tid="stat-card-received" accent="text-emerald-400" />
        <Stat label="Total sacado / pago" value={brl(b?.total_paid_out)} icon={ArrowUpFromLine} tid="stat-card-paid-out" accent="text-cyan-400" />
        <Stat label="Taxa da Gateway" value="0,00%" icon={Percent} tid="stat-card-fee" accent="text-amber-400" />
      </div>
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Entradas x saídas (14 dias)</h2>
          <span className="text-xs text-slate-500">Saques pendentes: {brl(b?.pending_payouts)}</span>
        </div>
        <div className="h-64" data-testid="volume-chart">
          <ResponsiveContainer>
            <AreaChart data={data?.chart || []}>
              <defs>
                <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06b6d4" stopOpacity={0.3} /><stop offset="100%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={60} />
              <Tooltip contentStyle={{ background: "#0b0f19", border: "1px solid #1e293b", borderRadius: 8 }} formatter={(v) => brl(v)} />
              <Area type="monotone" dataKey="entradas" stroke="#10b981" fill="url(#gIn)" strokeWidth={2} name="Entradas" />
              <Area type="monotone" dataKey="saidas" stroke="#06b6d4" fill="url(#gOut)" strokeWidth={2} name="Saídas" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card">
        <h2 className="mb-4 font-heading text-lg font-bold">Atividade recente</h2>
        <TxTable rows={data?.recent || []} />
      </div>
    </div>
  );
}
